/**
 * FleetManager V3 - Costs Widget
 * Monthly costs summary with breakdown - clickable to navigate to costs
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { CreditCard, ChevronRight } from 'lucide-react';
import { Card, CardHeader, CardTitle } from '../../shared/index.js';
import { formatMoney } from '../../../../utils/formatMoney.js';

const CATEGORY_COLORS = {
  maintenance: 'bg-blue-500',
  fuel: 'bg-emerald-500',
  insurance: 'bg-amber-500',
  other: 'bg-slate-500'
};

export function CostsWidget({ data = {}, onNavigate }) {
  const { t } = useTranslation();
  const { month = 0, breakdown = {}, currency = 'EUR' } = data;

  const formattedTotal = formatMoney(month, currency, { compact: true });

  const breakdownItems = Object.entries(breakdown).map(([key, value]) => ({
    key,
    label: t(`dashboard.costs.categories.${key}`, { defaultValue: key }),
    value,
    color: CATEGORY_COLORS[key] || CATEGORY_COLORS.other
  }));

  const handleClick = () => {
    onNavigate?.('costs');
  };

  return (
    <Card
      className="cursor-pointer hover:ring-1 hover:ring-blue-500/50 transition-all"
      onClick={handleClick}
    >
      <CardHeader>
        <CardTitle icon={CreditCard} iconColor="text-blue-400">{t('dashboard.costs.title')}</CardTitle>
        <div className="flex items-center gap-1">
          <span className="text-lg font-bold text-gray-900 dark:text-white">{formattedTotal}</span>
          <ChevronRight size={14} className="text-gray-400 dark:text-slate-500" />
        </div>
      </CardHeader>

      {breakdownItems.length > 0 && (
        <div className="space-y-2">
          {breakdownItems.map(item => (
            <div key={item.key} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${item.color}`} />
                <span className="text-xs text-gray-500 dark:text-slate-400">{item.label}</span>
              </div>
              <span className="text-sm text-gray-900 dark:text-white font-medium">
                {formatMoney(item.value, currency, { decimals: 0 })}
              </span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export default CostsWidget;
